import useGetPricingWorkloads from "@entities/pricingWorkload/lib/hooks/useGetPricingWorkloads";
import { IPricingWorkload } from "@shared/lib/types/pricingWorkload";
import { useMemo } from "react";
import { MY_PRICING_SEGMENT } from "../constants";

type UseMyPricingPocParams = {
  userIdParam: string;
  isSuperUser: boolean;
};

export function useMyPricingPoc({
  userIdParam,
  isSuperUser,
}: UseMyPricingPocParams) {
  const { pricingWorkloadData, isPricingWorkloadLoading } =
    useGetPricingWorkloads({
      userIds: [],
      enabled: isSuperUser,
    });

  const {
    pricingWorkloadData: pricingWorkloadPerUser,
    isPricingWorkloadLoading: isPricingWorkloadPerUserLoading,
  } = useGetPricingWorkloads({
    userIds: userIdParam ? [userIdParam] : [],
    enabled: Boolean(userIdParam),
  });

  const selectedPoc = useMemo(() => {
    return (
      pricingWorkloadPerUser?.data?.find(
        (item) => item.userId === userIdParam
      ) || null
    );
  }, [pricingWorkloadPerUser, userIdParam]);

  const displayWorkload: IPricingWorkload | null = useMemo(() => {
    return selectedPoc ?? pricingWorkloadPerUser?.data?.[0] ?? null;
  }, [selectedPoc, pricingWorkloadPerUser]);

  const pocSelectOptions =
    pricingWorkloadData?.data?.map((data) => ({
      label: `${data?.user?.firstName} ${data?.user?.lastName}`,
      value: data?.user?._id,
    })) ?? [];

  const pocSelectValue = pricingWorkloadData?.data?.find(
    (item) => item.userId === userIdParam
  )
    ? userIdParam
    : null;

  const segmentOptions = useMemo(() => {
    const activeCount = displayWorkload?.activeBriefsCount;
    const closedCount = displayWorkload?.closedBriefsCount;

    return [
      {
        label: `Active Briefs${activeCount != null ? ` (${activeCount})` : ""}`,
        value: MY_PRICING_SEGMENT.Open,
      },
      {
        label: `Closed Briefs${closedCount != null ? ` (${closedCount})` : ""}`,
        value: MY_PRICING_SEGMENT.Closed,
      },
    ];
  }, [displayWorkload]);

  const getStatValue = (
    key: keyof IPricingWorkload
  ): number | undefined => {
    const value = displayWorkload?.[key];
    return typeof value === "number" ? value : undefined;
  };

  return {
    pricingWorkloadData,
    isPricingWorkloadLoading,
    selectedPoc,
    displayWorkload,
    isPricingWorkloadPerUserLoading,
    pocSelectOptions,
    pocSelectValue,
    segmentOptions,
    getStatValue,
  };
}
